import { auth, db } from "../config/firebase";
import { arrayUnion, getDoc, doc, setDoc, updateDoc } from "firebase/firestore";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { ErrorMessage } from "@beyondrealityapp/core/shared/constants";
import { functions } from "../config/firebase";
import { HttpsCallable } from "@firebase/functions";
import { httpsCallable } from "firebase/functions";
import {
  AllowedModules,
  ManageBuddiesPayload,
  ManageBuddiesResponse,
  UpdateBuddySharingPayload,
  UpdateBuddySharingResponse,
} from "@beyondrealityapp/core/user/types";
import { Model } from "@beyondrealityapp/core/shared/constants";
import { NotificationSettings } from "@beyondrealityapp/core/user/types";
import { RhythmSettings } from "@beyondrealityapp/core/rhythm/types";
import { updateProfile, updateEmail } from "firebase/auth";
import { User } from "@beyondrealityapp/core/user/classes";

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["User"],
  endpoints: (builder) => ({
    fetchUser: builder.query({
      queryFn: async (id: string | undefined) => {
        if (!id) {
          return { error: new Error("No user ID provided") };
        }
        try {
          const user = await _fetchUser(id);
          return { data: user };
        } catch (error) {
          return { error: error as Error };
        }
      },
      providesTags: ["User"],
    }),
    updateUser: builder.mutation({
      queryFn: async (user: User) => {
        try {
          await _updateUser(user);
          return { data: user };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["User"],
    }),
    addBuddy: builder.mutation({
      queryFn: async ({
        buddyEmail,
        allowedModules,
        updateExistingRecords,
      }: {
        buddyEmail: string;
        allowedModules?: AllowedModules;
        updateExistingRecords?: boolean;
      }) => {
        try {
          const response = await _addBuddy(buddyEmail, allowedModules, updateExistingRecords);
          return { data: response };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["User"],
    }),
    removeBuddy: builder.mutation({
      queryFn: async (buddyId: string) => {
        try {
          const response = await _removeBuddy(buddyId);
          return { data: response };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["User"],
    }),
    updateBuddySharing: builder.mutation({
      queryFn: async (payload: UpdateBuddySharingPayload) => {
        try {
          const response = await _updateBuddySharing(payload);
          return { data: response };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["User"],
    }),
    updateNotificationSettings: builder.mutation({
      queryFn: async (notificationSettings: NotificationSettings) => {
        try {
          await _updateNotificationSettings(notificationSettings);
          return { data: notificationSettings };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["User"],
    }),
    updateRhythm: builder.mutation({
      queryFn: async (rhythm: RhythmSettings) => {
        try {
          await _updateRhythm(rhythm);
          return { data: rhythm };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["User"],
    }),
    persistPushNotificationToken: builder.mutation({
      queryFn: async (token: string) => {
        try {
          await _persistPushNotificationToken(token);
          return { data: token };
        } catch (error) {
          return { error: error as Error };
        }
      },
    }),
  }),
});

export const {
  useFetchUserQuery,
  useUpdateUserMutation,
  useAddBuddyMutation,
  useRemoveBuddyMutation,
  useUpdateBuddySharingMutation,
  useUpdateNotificationSettingsMutation,
  useUpdateRhythmMutation,
  usePersistPushNotificationTokenMutation,
} = userApi;

export const _fetchUser = async (id: string) => {
  return new Promise<User>((resolve, reject) => {
    const userRef = doc(db, Model.USERS, id);
    getDoc(userRef)
      .then((doc) => {
        if (doc.exists()) {
          const user = User.fromFirestore(doc.data());
          resolve(user);
        } else {
          reject(new Error(ErrorMessage.NO_ID));
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const _updateUser = async (user: User) => {
  user.init(auth);
  return new Promise<void>((resolve, reject) => {
    if (auth.currentUser) {
      // Force token refresh so Firestore security rules see the latest
      // custom claims (e.g. subscriptionStatus set by the setupUser trigger).
      // Without this, a freshly signed-up user's cached token lacks claims
      // and the update rule's hasActiveSubscription() check would fail.
      auth.currentUser
        .getIdToken(true)
        .then(() =>
          updateProfile(auth.currentUser!, {
            displayName: user.name,
            photoURL: user.profilePicture,
          })
        )
        .then(() => {
          const userRef = doc(db, Model.USERS, user.uid);
          setDoc(userRef, user.toFirestoreForClient(), {
            merge: true,
          })
            .then(() => {
              resolve();
            })
            .catch((error) => {
              reject(error);
            });
        })
        .catch((error) => {
          reject(error);
        });
    }
    if (auth.currentUser && auth.currentUser.email !== user.email) {
      updateEmail(auth.currentUser, user.email)
        .then(() => {
          const userRef = doc(db, Model.USERS, user.uid);
          setDoc(userRef, user.toFirestoreForClient(), {
            merge: true,
          })
            .then(() => {
              resolve();
            })
            .catch((error) => {
              reject(error);
            });
        })
        .catch((error) => {
          reject(error);
        });
    }
  });
};

const _addBuddy = async (
  buddyEmail: string,
  allowedModules?: AllowedModules,
  updateExistingRecords?: boolean
) => {
  return new Promise<ManageBuddiesResponse>((resolve, reject) => {
    const manageBuddies: HttpsCallable<
      ManageBuddiesPayload,
      ManageBuddiesResponse
    > = httpsCallable(functions, "manageBuddies");

    manageBuddies({
      action: "add",
      buddyEmail,
      allowedModules,
      updateExistingRecords,
    })
      .then((status) => {
        resolve(status.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const _removeBuddy = async (buddyEmail: string) => {
  return new Promise<void>((resolve, reject) => {
    const manageBuddies: HttpsCallable<
      ManageBuddiesPayload,
      ManageBuddiesResponse
    > = httpsCallable(functions, "manageBuddies");

    manageBuddies({
      action: "remove",
      buddyEmail,
    })
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const _updateNotificationSettings = async (
  notificationSettings: NotificationSettings
) => {
  return new Promise<void>((resolve, reject) => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      reject(new Error(ErrorMessage.NO_ID));
      return;
    }
    const userRef = doc(db, Model.USERS, uid);
    setDoc(userRef, { notifications: notificationSettings }, { merge: true })
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const _updateRhythm = async (rhythm: RhythmSettings) => {
  const uid = auth.currentUser?.uid;
  if (!uid) {
    throw new Error(ErrorMessage.NO_ID);
  }
  // Replace the whole map, so a weekday removed from an item doesn't linger
  await updateDoc(doc(db, Model.USERS, uid), { rhythm });
};

const _persistPushNotificationToken = async (token: string) => {
  return new Promise<void>((resolve, reject) => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      reject(new Error(ErrorMessage.NO_ID));
      return;
    }

    const pushNotificationToken = `pushNotificationToken${uid}`;

    const localToken = localStorage.getItem(pushNotificationToken);
    if (localToken === token) {
      resolve();
      return;
    }

    localStorage.setItem(pushNotificationToken, token);

    const userRef = doc(db, Model.USERS, uid);
    setDoc(
      userRef,
      { pushNotificationTokens: arrayUnion(token) },
      { merge: true }
    )
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const _updateBuddySharing = async (payload: UpdateBuddySharingPayload) => {
  return new Promise<UpdateBuddySharingResponse>((resolve, reject) => {
    const updateBuddySharing: HttpsCallable<
      UpdateBuddySharingPayload,
      UpdateBuddySharingResponse
    > = httpsCallable(functions, "updateBuddySharing");

    updateBuddySharing(payload)
      .then((result) => {
        resolve(result.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};
